/**
 * WeatherTimeline Component
 *
 * Visar en horisontell eller vertikal tidslinje med väderpunkter
 * längs rutten. Snyggt UI med animationer och tydlig visuell hierarki.
 */

import { getWeatherEmoji } from '@/services/smhi';
import type { RouteWeatherPoint } from '@/types';

interface WeatherTimelineProps {
  weatherPoints: RouteWeatherPoint[];
  orientation?: 'horizontal' | 'vertical';
}

/**
 * Formatera tid (HH:mm)
 */
function formatTime(date: Date): string {
  return date.toLocaleTimeString('sv-SE', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Formatera avstånd i km
 */
function formatDistance(km: number): string {
  if (km < 1) {
    return `${Math.round(km * 1000)} m`;
  }
  return `${Math.round(km)} km`;
}

export function WeatherTimeline({
  weatherPoints,
  orientation = 'horizontal',
}: WeatherTimelineProps) {
  if (weatherPoints.length === 0) {
    return null;
  }

  const isHorizontal = orientation === 'horizontal';

  return (
    <div className={`
      ${isHorizontal
        ? 'overflow-x-auto pb-4 -mx-4 px-4 scrollbar-thin scrollbar-thumb-gray-300'
        : 'space-y-4'
      }
    `}>
      <div className={`
        ${isHorizontal
          ? 'flex gap-3 min-w-max'
          : 'relative'
        }
      `}>
        {/* Vertikal linje för vertikal layout */}
        {!isHorizontal && (
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 via-cyan-500 to-green-500" />
        )}

        {weatherPoints.map((point, index) => {
          const isFirst = index === 0;
          const isLast = index === weatherPoints.length - 1;
          const hasWarning = point.warnings.length > 0;

          return (
            <div
              key={`${point.location.lat}-${point.location.lon}-${index}`}
              className={`
                animate-fade-in
                ${isHorizontal ? 'flex-shrink-0' : 'relative pl-14'}
              `}
              style={{ animationDelay: `${index * 50}ms` }}
            >
              {/* Punkt på linjen (vertikal) */}
              {!isHorizontal && (
                <div className={`
                  absolute left-4 top-4 w-4 h-4 rounded-full border-2 border-white shadow-md z-10
                  ${isFirst
                    ? 'bg-green-500'
                    : isLast
                    ? 'bg-red-500'
                    : hasWarning
                    ? 'bg-orange-500'
                    : 'bg-blue-500'
                  }
                `} />
              )}

              {/* Väderkort */}
              <TimelineCard
                point={point}
                isFirst={isFirst}
                isLast={isLast}
                isHorizontal={isHorizontal}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface TimelineCardProps {
  point: RouteWeatherPoint;
  isFirst: boolean;
  isLast: boolean;
  isHorizontal: boolean;
}

function TimelineCard({ point, isFirst, isLast, isHorizontal }: TimelineCardProps) {
  const { weather, distanceFromStart, estimatedArrival, warnings, location } = point;
  const hasWarning = warnings.length > 0;
  const highestWarning = warnings[0];

  const warningColor = hasWarning
    ? highestWarning?.level === 'RED'
      ? 'border-red-400'
      : highestWarning?.level === 'ORANGE'
      ? 'border-orange-400'
      : highestWarning?.level === 'YELLOW'
      ? 'border-yellow-400'
      : 'border-blue-400'
    : 'border-transparent';

  return (
    <div
      className={`
        bg-white rounded-xl shadow-md border-2 ${warningColor}
        transition-all duration-200 hover:shadow-lg hover:-translate-y-1
        ${isHorizontal ? 'w-32 p-3' : 'p-4'}
      `}
    >
      {/* Header */}
      <div className={`flex items-center justify-between ${isHorizontal ? 'mb-2' : 'mb-3'}`}>
        <span className={`
          ${isHorizontal ? 'text-xs' : 'text-sm'}
          font-medium
          ${isFirst ? 'text-green-600' : isLast ? 'text-red-600' : 'text-gray-600'}
        `}>
          {isFirst
            ? '🚗 Start'
            : isLast
            ? '🏁 Mål'
            : formatDistance(distanceFromStart)}
        </span>
      </div>

      {/* Väderikon och temperatur */}
      <div className={`flex items-center ${isHorizontal ? 'gap-2' : 'gap-3'}`}>
        <span className={isHorizontal ? 'text-3xl' : 'text-4xl'}>
          {getWeatherEmoji(weather.weatherSymbol, location, estimatedArrival)}
        </span>
        <div>
          <div className={`font-bold text-gray-900 ${isHorizontal ? 'text-xl' : 'text-2xl'}`}>
            {Math.round(weather.temperature)}°
          </div>
          <div className={`text-gray-500 ${isHorizontal ? 'text-xs' : 'text-sm'}`}>
            {formatTime(estimatedArrival)}
          </div>
        </div>
      </div>

      {/* Extra info (endast för vertikal layout) */}
      {!isHorizontal && (
        <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-500">
          <div>💨 {Math.round(weather.windSpeed)} m/s</div>
          <div>💧 {weather.precipitation.toFixed(1)} mm</div>
        </div>
      )}

      {/* Varnings-badge */}
      {hasWarning && highestWarning && (
        <div className={`
          mt-2 text-xs font-medium truncate
          ${highestWarning.level === 'RED'
            ? 'text-red-600'
            : highestWarning.level === 'ORANGE'
            ? 'text-orange-600'
            : highestWarning.level === 'YELLOW'
            ? 'text-yellow-600'
            : 'text-blue-600'
          }
        `}>
          ⚠️ Varning
        </div>
      )}
    </div>
  );
}
